
import { toast } from "sonner";

/**
 * Security utilities to enhance application protection
 */

// Enhanced CSP Headers setup helper
export const setupCSP = () => {
  // Note: In production, these headers should be set server-side via edge function
  // This provides client-side fallback for development
  if (typeof document !== "undefined") {
    // Set comprehensive Content-Security-Policy
    const cspMeta = document.createElement("meta");
    cspMeta.httpEquiv = "Content-Security-Policy";
    cspMeta.content = [
      "default-src 'self'",
      "script-src 'self' 'unsafe-inline' https://breqydpjtdocbmqcrqhi.supabase.co",
      "style-src 'self' 'unsafe-inline'",
      "img-src 'self' data: https:",
      "connect-src 'self' https://breqydpjtdocbmqcrqhi.supabase.co wss://breqydpjtdocbmqcrqhi.supabase.co https://*.supabase.co",
      "font-src 'self' data:",
      "object-src 'none'",
      "base-uri 'self'",
      "form-action 'self'",
      "frame-ancestors 'none'",
      "upgrade-insecure-requests"
    ].join('; ');
    document.head.appendChild(cspMeta);
    
    // Additional security headers via meta tags
    const xfoMeta = document.createElement("meta");
    xfoMeta.httpEquiv = "X-Frame-Options";
    xfoMeta.content = "DENY";
    document.head.appendChild(xfoMeta);
    
    const xctMeta = document.createElement("meta");
    xctMeta.httpEquiv = "X-Content-Type-Options";
    xctMeta.content = "nosniff";
    document.head.appendChild(xctMeta);
    
    const referrerMeta = document.createElement("meta");
    referrerMeta.name = "referrer";
    referrerMeta.content = "strict-origin-when-cross-origin";
    document.head.appendChild(referrerMeta);
    
    console.log("[Security] Enhanced CSP and security headers applied");
  }
};

// Generate secure session IDs
export const generateSecureId = (length = 32): string => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const randomValues = new Uint8Array(length);
  if (typeof window !== "undefined" && window.crypto) {
    window.crypto.getRandomValues(randomValues);
    let result = '';
    for (let i = 0; i < length; i++) {
      result += characters[randomValues[i] % characters.length];
    }
    return result;
  }
  // Fallback to less secure but functional method if crypto isn't available
  return Array.from({ length }, () => 
    characters.charAt(Math.floor(Math.random() * characters.length))
  ).join('');
};

// Rate limiting for client-side API calls
const apiCallRegistry: Record<string, { count: number, lastReset: number }> = {};

export const checkRateLimit = (endpoint: string, limit = 10, windowMs = 60000): boolean => {
  const now = Date.now();
  
  if (!apiCallRegistry[endpoint] || (now - apiCallRegistry[endpoint].lastReset > windowMs)) {
    apiCallRegistry[endpoint] = { count: 1, lastReset: now };
    return true;
  }
  
  if (apiCallRegistry[endpoint].count >= limit) {
    toast.error("Too many requests. Please wait before trying again.");
    console.warn(`[Security] Rate limit reached for ${endpoint}`);
    return false;
  }
  
  apiCallRegistry[endpoint].count += 1;
  return true;
};

// Data obfuscation for sensitive information display
export const obfuscateEmail = (email: string): string => {
  if (!email || typeof email !== 'string') return '';
  
  const [username, domain] = email.split('@');
  if (!domain) return email;
  
  const obfuscatedUsername = username.length <= 2 
    ? username 
    : `${username.slice(0, 2)}${'*'.repeat(username.length - 2)}`;
  
  return `${obfuscatedUsername}@${domain}`;
};

// Check for suspicious activity patterns
export const detectSuspiciousActivity = (event: any): boolean => {
  // Define patterns of suspicious activity
  const suspiciousPatterns = [
    { type: 'rapid-fire', threshold: 10, timeWindow: 5000 },
    { type: 'data-scraping', threshold: 50, timeWindow: 30000 }
  ];
  
  // Implementation would track user actions and detect patterns
  // This is a placeholder for demonstration purposes
  
  return false;
};

// Initialize security monitoring
export const initSecurityMonitoring = () => {
  // Set up security monitoring hooks
  if (typeof window !== 'undefined') {
    // Listen for paste events to detect potential automated form filling
    document.addEventListener('paste', (e) => {
      const inputType = (e.target as HTMLElement)?.tagName;
      if (inputType === 'INPUT' || inputType === 'TEXTAREA') {
        console.log('[Security] Paste event detected in form field');
      }
    });
    
    // Track suspicious navigation patterns
    let navigationCount = 0;
    let lastNavTime = Date.now();
    
    window.addEventListener('popstate', () => {
      const now = Date.now();
      if (now - lastNavTime < 1000) {
        navigationCount++;
        if (navigationCount > 5) {
          console.warn('[Security] Suspicious rapid navigation detected');
        }
      } else {
        navigationCount = 0;
      }
      lastNavTime = now;
    });
    
    console.log('[Security] Monitoring initialized');
  }
};
